import { createSurveyStyles } from '@sm/webassets/SurveyTheme/context';
import { WrenchTheme } from '@wds/styles';
import { getContrastColor } from '@wds/utils';
import { getContainerBackgroundColor, isTransparent, isV2Theme } from '~app/helpers/themeLayout';

const FALLBACK_COLOR = '#000';

const getV2AccentColor = ({
  topSectionHasLogo,
  logoBackgroundColor,
  surveyTitleBackgroundColor,
  titleColor = FALLBACK_COLOR,
  isSurveyTitleEnabled,
  pageTitleBackgroundColor,
}: {
  topSectionHasLogo: boolean;
  logoBackgroundColor?: string;
  surveyTitleBackgroundColor?: string;
  titleColor?: string;
  isSurveyTitleEnabled?: boolean;
  pageTitleBackgroundColor?: string;
}): string => {
  if (!logoBackgroundColor) {
    return titleColor;
  }

  if (isTransparent(logoBackgroundColor)) {
    return FALLBACK_COLOR;
  }

  const backgroundColor = getContainerBackgroundColor({
    logoBackgroundColor,
    surveyTitleBackgroundColor,
    isLogoEnabled: topSectionHasLogo,
    isSurveyTitleEnabled,
    pageTitleBackgroundColor,
  });

  const contrastColor = getContrastColor(backgroundColor);
  return contrastColor === 'dark' ? '#000' : '#fff';
};

type LanguageSelectStyleProps = {
  topSectionHasLogo: boolean;
  isSurveyTitleEnabled: boolean;
};

const useStyles = createSurveyStyles<'selectContainer', LanguageSelectStyleProps>(theme => {
  const getAccentColor = (topSectionHasLogo: boolean, isSurveyTitleEnabled: boolean): string => {
    return isV2Theme(theme.version)
      ? getV2AccentColor({
          topSectionHasLogo,
          logoBackgroundColor: theme.logoStyle?.backgroundColor,
          surveyTitleBackgroundColor: theme.surveyTitle?.backgroundColor,
          titleColor: theme.titleColor,
          isSurveyTitleEnabled,
          pageTitleBackgroundColor: theme.pageTitle?.backgroundColor,
        })
      : theme.titleColor ?? '#fff';
  };

  return {
    selectContainer: {
      fontWeight: 400,
      fontFamily: WrenchTheme.type.fontFamily.base,
      fontSize: WrenchTheme.type.fontSize.body, // 16px
      color: ({ topSectionHasLogo, isSurveyTitleEnabled }) => getAccentColor(topSectionHasLogo, isSurveyTitleEnabled),
      borderRadius: 0,
      '& select': {
        backgroundColor: 'transparent',
        paddingRight: '1.5em', // 24px ((6px+6px+12px)÷16px)
        '&:focus-within, &:hover': {
          border: ({ topSectionHasLogo, isSurveyTitleEnabled }) =>
            `1px solid ${getAccentColor(topSectionHasLogo, isSurveyTitleEnabled)}`,
        },
      },
      '& option': {
        color: '#000',
      },
      '& svg': {
        width: `0.75em`, // 12px (12px÷16px)
        height: `0.75em`, // 12px (12px÷16px)
      },
    },
  };
});

export default useStyles;
